import React from "react";
import { DollarSign } from "lucide-react";
import { Input } from "./ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { COST_UNIT_OPTS, unitLabel } from "../lib/framing";

const Row = ({ testid, label, unitKey, priceKey, options, form, onChange, currency }) => (
  <div className="grid grid-cols-[80px_1fr_88px] items-center gap-2" data-testid={`cost-row-${testid}`}>
    <span className="text-sm font-medium text-foreground/80">{label}</span>
    <Select value={form[unitKey] || undefined} onValueChange={(v) => onChange(unitKey, v)}>
      <SelectTrigger data-testid={`cost-unit-${testid}`} className="h-9"><SelectValue placeholder="Unit type" /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{unitLabel(o)}</SelectItem>)}
      </SelectContent>
    </Select>
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{currency}</span>
      <Input data-testid={`cost-price-${testid}`} type="number" inputMode="decimal" min="0" placeholder="0.00"
        value={form[priceKey]} onChange={(e) => onChange(priceKey, e.target.value)} className="h-9 pl-6 font-mono text-sm" />
    </div>
  </div>
);

export const CostInputs = ({ form, onChange, result, unit }) => {
  const currency = form.currency || "$";
  const hasMat = result?.total_mat_width > 0;
  const spacerType = result?.spacer_type;
  const hasSpacer = spacerType && spacerType !== "none";
  const spacerOpts = COST_UNIT_OPTS[`spacer_${spacerType}`] || [];

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="cost-inputs">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-1.5 font-heading text-base font-bold">
          <DollarSign className="h-4 w-4 text-primary" /> Component costs
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Currency</label>
          <Input data-testid="cost-currency" value={form.currency} onChange={(e) => onChange("currency", e.target.value.slice(0, 4))} className="h-9 w-16 text-center font-mono" />
        </div>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">Pick how you buy each component and the price you paid per unit — costs are worked out from your cut sizes.</p>

      <div className="space-y-3">
        {hasMat && (
          <Row testid="matboard" label="Matboard" unitKey="mat_cost_unit" priceKey="mat_cost_price" options={COST_UNIT_OPTS.matboard} form={form} onChange={onChange} currency={currency} />
        )}
        <Row testid="moulding" label="Moulding" unitKey="moulding_cost_unit" priceKey="moulding_cost_price" options={COST_UNIT_OPTS.moulding} form={form} onChange={onChange} currency={currency} />
        <Row testid="glazing" label="Glazing" unitKey="glazing_cost_unit" priceKey="glazing_cost_price" options={COST_UNIT_OPTS.glazing} form={form} onChange={onChange} currency={currency} />
        {form.glazing_cost_unit === "custom" && (
          <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-1">
            <span className="text-xs text-muted-foreground">Sheet size</span>
            <div className="flex items-center gap-2">
              <Input data-testid="glazing-custom-w" type="number" inputMode="decimal" min="0" placeholder="width" value={form.glazing_custom_w} onChange={(e) => onChange("glazing_custom_w", e.target.value)} className="h-9 font-mono text-sm" />
              <span className="text-muted-foreground">×</span>
              <Input data-testid="glazing-custom-h" type="number" inputMode="decimal" min="0" placeholder="height" value={form.glazing_custom_h} onChange={(e) => onChange("glazing_custom_h", e.target.value)} className="h-9 font-mono text-sm" />
              <span className="text-xs text-muted-foreground">{unit}</span>
            </div>
          </div>
        )}
        {hasSpacer && (
          <Row testid="spacer" label="Spacer" unitKey="spacer_cost_unit" priceKey="spacer_cost_price" options={spacerOpts} form={form} onChange={onChange} currency={currency} />
        )}
      </div>
    </div>
  );
};
